import type { EngineContext, TransmitterData } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import {
  resolveIrsIntakeTaxYear,
  schemaVersionForTaxYear,
  softwareIdForTaxYear,
} from "../../constants/taxYear";
import {
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { Record1099Q } from "./interface/1099-Q-interface";
import { form1099QBoxes, form1099QTotals, recipientDetail1099Q } from "./detail";

/**
 * The IRTransmission envelope for one Form 1099-Q submission. Shared with the
 * correction generator, which passes TransmissionTypeCd "C".
 *
 * The same envelope as the Form 1098 one: the transmitter, the TCC and the Software ID come
 * from the EngineContext. 1099-Q has no state boxes, so there is no CFSF election to make.
 */
export function build1099QTransmission(
  ctx: EngineContext,
  details: object[],
  records: Record1099Q[],
  taxYear: string | number,
  transmitterData: TransmitterData,
  transmissionTypeCd: "O" | "C",
): object {
  return {
    "?xml": { "@_version": "1.0", "@_encoding": "UTF-8" },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation": "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: generateUniqueTransmissionId(ctx.irs.tcc),
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: transmissionTypeCd,
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: "1",
        TotalRecipientFormCnt: details.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: generateUniqueSubmissionId(),
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099Q",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0],
              JuratPersonTitleTxt: "CFO",
              PersonNm: transmitterData.contactName || "Payer",
            },
            TotalReportedRcpntFormCnt: details.length.toString(),
            IRSubmission1FormTotals: {
              Form1099QTotalAmtGrp: form1099QTotals(records),
            },
          },
          IRSubmission1Detail: {
            Form1099QDetail: details,
          },
        },
      ],
    },
  };
}

export const xmlBuilder = () =>
  new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: false,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

/**
 * Form 1099-Q (Payments From Qualified Education Programs) original transmission.
 * Records are canonical 1099-Q records; see ./detail.ts for the column mapping.
 */
export async function generate1099QXml(ctx: EngineContext, records: Record1099Q[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        { error: true, message: "No records found or records input is invalid." },
      ],
      xml: null,
      taxYear: null,
    };
  }

  const transmitterData = ctx.transmitter;
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  const testFile = transmitterData.testFileIndicator === "T";

  const details = records.map((record) => ({
    "@_xmlns": "urn:us:gov:treasury:irs:ir",
    "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
    TaxYr: taxYear.toString(),
    RecordId: record.id.toString(),
    VoidInd: "0",
    CorrectedInd: record.corrected ? "1" : "0",
    RecipientDetail: recipientDetail1099Q(record, testFile, ctx.schemaRoot),
    ...form1099QBoxes(record),
  }));

  try {
    const xml = xmlBuilder().build(
      build1099QTransmission(ctx, details, records, taxYear, transmitterData, "O"),
    );
    return { xml, taxYear, validationResponse: [] };
  } catch (error) {
    console.error("Error generating 1099-Q XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear,
    };
  }
}
